import { io, Socket } from 'socket.io-client';
import { getRuntimeServerUrl, isNativeShell } from './runtimeConfig';
import { playSoundEvent } from './sound';

let socket: Socket | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let connectivityListenersBound = false;
let connectedOnce = false;

function ensureHeartbeat() {
  if (!socket) return;
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => {
    try {
      if (!socket || !socket.connected) return;
      socket.emit('ping');
    } catch {}
  }, 30_000);
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function bindConnectivityListeners() {
  if (connectivityListenersBound) return;
  if (typeof window === 'undefined') return;
  connectivityListenersBound = true;

  const reconnect = () => {
    try {
      if (socket && !socket.connected) socket.connect();
    } catch {}
  };

  window.addEventListener('online', reconnect);
  window.addEventListener('focus', reconnect);
  window.addEventListener('pageshow', reconnect);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reconnect();
  });
}

function getToken(): string | null {
  try {
    return localStorage.getItem('al_jwt');
  } catch {
    return null;
  }
}

export function connectSocket(): Socket {
  bindConnectivityListeners();
  const token = getToken();
  if (socket) {
    try {
      const cur = (socket as any).auth?.token;
      if (token && cur !== token) {
        (socket as any).auth = { ...(socket as any).auth, token };
        try {
          if (socket.connected) socket.disconnect();
        } catch {}
        try { socket.connect(); } catch {}
      } else {
        try {
          if (!socket.connected) socket.connect();
        } catch {}
      }
    } catch {}
    ensureHeartbeat();
    return socket;
  }

  const baseUrl = (() => {
    try {
      if (!isNativeShell()) return '/';
      const u = getRuntimeServerUrl();
      return u ? u : '/';
    } catch {
      return '/';
    }
  })();

  socket = io(baseUrl, {
    path: '/socket.io',
    withCredentials: true,
    transports: ['websocket', 'polling'],
    auth: token ? { token } : undefined,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 15_000,
    randomizationFactor: 0.5,
    timeout: 20_000,
  });
  socket.on('disconnect', () => {
    if (!connectedOnce) return;
    try { playSoundEvent('connection_lost'); } catch {}
  });
  socket.on('connect', () => {
    if (connectedOnce) {
      try { playSoundEvent('connection_restored'); } catch {}
    }
    connectedOnce = true;
  });
  ensureHeartbeat();
  return socket;
}

export function getSocket(): Socket | null {
  return socket;
}

export function disconnectSocket() {
  try {
    socket?.disconnect();
  } finally {
    socket = null;
    stopHeartbeat();
    connectedOnce = false;
  }
}

export type InvalidateScope = 'absences' | 'calendar' | 'chats' | 'files' | 'journal' | 'addressbook' | 'requests' | 'users' | 'settings' | 'notifications' | 'permissions' | 'admin';

export type InvalidateEvent = {
  scope: InvalidateScope;
  kind?: string;
  [k: string]: any;
};

export function onInvalidate(handler: (e: InvalidateEvent) => void, scopes?: InvalidateScope[]) {
  const s = connectSocket();
  const allowed = Array.isArray(scopes) && scopes.length > 0 ? new Set(scopes) : null;
  const wrapped = (e: any) => {
    const ev = (e || {}) as InvalidateEvent;
    if (!ev.scope) return;
    if (allowed && !allowed.has(ev.scope)) return;
    handler(ev);
  };
  s.on('invalidate', wrapped);
  return () => {
    try { s.off('invalidate', wrapped); } catch {}
  };
}
